import { PlusOutlined, SearchOutlined, VideoCameraOutlined } from '@ant-design/icons'
import { Button, Empty, Input, Pagination, Skeleton, Tag, message } from 'antd'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import { formatDateTime as formatDateTimeI18n } from '../../../i18n/dateFormat'
import { useUiLocale } from '../../../i18n/LocaleProvider'
import {
  createBlankVideoDirectorProject,
  listVideoDirectorProjects,
  type SaasApiError,
  type VideoDirectorProjectStage,
  type VideoDirectorProjectSummary,
} from '../../api'
import '../../ui/tenant-items.css'
import './video-project-list.css'

const PAGE_SIZE = 24

function projectStageLabel(stage: VideoDirectorProjectStage, t: (key: string) => string): string {
  const map: Record<VideoDirectorProjectStage, string> = {
    IDEA: t('video:director.steps.1'),
    DIRECTOR_PROPOSAL: t('video:director.steps.1'),
    STORYBOARD: t('video:director.steps.2'),
    KEYFRAME_DESIGN: t('video:director.steps.3'),
    VIDEO_GENERATION: t('video:director.steps.4'),
    RESULT: t('video:director.steps.5'),
  }
  return map[stage] ?? stage
}

function projectEditorUrl(workspaceId: string, projectId: string) {
  return `/workspace/${workspaceId}/video-generation?session_id=${encodeURIComponent(projectId)}`
}

export default function VideoProjectListPage() {
  const { t } = useTranslation(['video', 'actions'])
  const { locale } = useUiLocale()
  const { workspaceId = '' } = useParams()
  const navigate = useNavigate()
  const [q, setQ] = useState('')
  const [debouncedQ, setDebouncedQ] = useState('')
  const [page, setPage] = useState(1)
  const [items, setItems] = useState<VideoDirectorProjectSummary[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(false)
  const [creating, setCreating] = useState(false)

  const creationModeLabel = (mode: string) => {
    const key = `video:projectList.creationMode.${mode}` as const
    return t(key, { defaultValue: mode })
  }

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedQ(q.trim())
      setPage(1)
    }, 300)
    return () => window.clearTimeout(timer)
  }, [q])

  const load = useCallback(async () => {
    if (!workspaceId) return
    setLoading(true)
    try {
      const { data } = await listVideoDirectorProjects(workspaceId, {
        q: debouncedQ || undefined,
        page,
        page_size: PAGE_SIZE,
      })
      setItems(data.items)
      setTotal(data.total)
    } catch (e) {
      message.error((e as SaasApiError).message || t('video:projectList.toast.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [workspaceId, debouncedQ, page, t])

  useEffect(() => {
    void load()
  }, [load])

  const onCreate = async () => {
    if (!workspaceId) return
    setCreating(true)
    try {
      const { data } = await createBlankVideoDirectorProject(workspaceId)
      const projectId = data.session_id || data.project?.project_id
      if (!projectId) throw new Error(t('video:projectList.toast.createMissingId'))
      message.success(t('video:projectList.toast.createSuccess'))
      navigate(projectEditorUrl(workspaceId, projectId))
    } catch (e) {
      message.error((e as SaasApiError).message || t('video:projectList.toast.createFailed'))
    } finally {
      setCreating(false)
    }
  }

  const openProject = (project: VideoDirectorProjectSummary) => {
    navigate(projectEditorUrl(workspaceId, project.project_id))
  }

  return (
    <div className="tc-items-page">
      <div className="tc-page-header">
        <div>
          <h2 className="tc-page-title">{t('video:projectList.title')}</h2>
          <p className="tc-page-desc">{t('video:projectList.subtitle')}</p>
        </div>
        <div className="tc-page-actions">
          <Button type="primary" icon={<PlusOutlined />} loading={creating} onClick={() => void onCreate()}>
            {t('video:projectList.newVideo')}
          </Button>
        </div>
      </div>

      <div className="tc-data-card">
        <div className="tc-toolbar" style={{ justifyContent: 'flex-end' }}>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder={t('video:projectList.searchPlaceholder')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{ width: 240 }}
          />
        </div>

        {loading ? (
          <div className="tc-vpl-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} active paragraph={{ rows: 3 }} />
            ))}
          </div>
        ) : items.length === 0 ? (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <div>
                <div style={{ fontWeight: 600, marginBottom: 6 }}>{t('video:projectList.empty.title')}</div>
                <div style={{ color: 'var(--tc-text-secondary)' }}>{t('video:projectList.empty.description')}</div>
              </div>
            }
          >
            <Button type="primary" icon={<PlusOutlined />} onClick={() => void onCreate()}>
              {t('video:projectList.newVideo')}
            </Button>
          </Empty>
        ) : (
          <>
            <div className="tc-vpl-grid">
              {items.map((project) => (
                <button
                  key={project.project_id}
                  type="button"
                  className="tc-vpl-card"
                  onClick={() => openProject(project)}
                >
                  <div className="tc-vpl-card-thumb">
                    <VideoCameraOutlined />
                  </div>
                  <div className="tc-vpl-card-body">
                    <div className="tc-vpl-card-title" data-i18n-exempt="business-data">{project.title}</div>
                    <div className="tc-vpl-card-meta">{creationModeLabel(project.creation_mode)}</div>
                    <div className="tc-vpl-card-meta">
                      {t('video:projectList.durationSeconds', {
                        seconds: project.duration_seconds,
                        aspectRatio: project.aspect_ratio,
                      })}
                    </div>
                    <div className="tc-vpl-card-stage">
                      <span className="tc-vpl-card-stage-label">{t('video:projectList.currentStage')}</span>
                      <Tag>{projectStageLabel(project.project_stage, t)}</Tag>
                    </div>
                    <div className="tc-vpl-card-updated">
                      {t('video:projectList.lastEdited', {
                        time: project.updated_at ? formatDateTimeI18n(project.updated_at, locale) : '—',
                      })}
                    </div>
                    <div className="tc-vpl-card-action">{t('video:projectList.continueEditing')}</div>
                  </div>
                </button>
              ))}
            </div>
            {total > PAGE_SIZE && (
              <div className="tc-vpl-pagination">
                <Pagination
                  current={page}
                  pageSize={PAGE_SIZE}
                  total={total}
                  showSizeChanger={false}
                  onChange={(p) => setPage(p)}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
